import React, { useState, useMemo, useEffect, memo } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  ColumnDef,
} from '@tanstack/react-table';
import {
  FUNDING_RATE_DECIMALS,
  MarketStatus,
  useMarketStore,
  useMarketStores,
} from '@/store/use-markets-store';
import { bigIntToNum, formatBigInt, formatNumber } from '@/utils/value-format';
import {
  abbrFromSym,
  imgFromAbbr,
  symbolToBaseQuote,
} from '@/utils/token-symbol';
import { cn } from '@/utils/cn';
import { MarketRow } from './market-row';
import { FavoriteButton } from './favorite-button';
import { Search } from 'lucide-react';
import { getMaxLeverage } from '@/features/trade/order/utils/math';
import { useFavorites } from '@/hooks';
import { useStore } from 'zustand';
import { useTicker24hr } from '../api/get-ticker-24hr';
import useOpenInterest from '../api/get-open-interest';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui';
import { SYMBOL_CATEGORIES, SymbolCategory } from '@/app/pages/markets';
import TooltipWrapper from '@/components/ui/tooltip';
import { Link } from 'react-router-dom';

interface MarketItem {
  symbol: string;
  leverage?: string;
  markPrice: bigint;
  priceChange: bigint;
  priceChangePct: bigint;
  fundingRate: bigint;
  openInterest?: bigint;
  quoteVolume?: bigint;
  isolated: boolean;
  status: MarketStatus;
}

interface MarketsTableProps {
  onRowClick: (symbol: string) => void;
}

const columns: ColumnDef<MarketItem>[] = [
  {
    accessorKey: 'symbol',
    header: 'Symbol',
    cell: ({ row }) => (
      <div className="flex items-center gap-1.5 sm:gap-3">
        <div className="sm:mr-1.5">
          <FavoriteButton symbol={row.original.symbol} size={14} />
        </div>
        <img
          src={imgFromAbbr(abbrFromSym(row.original.symbol))}
          alt="symbol"
          className="h-5 w-5 max-sm:mx-1"
        />
        <span className="hidden font-sans md:inline">
          {symbolToBaseQuote(row.original.symbol)}
        </span>
        <span className="font-sans md:hidden">
          {abbrFromSym(row.original.symbol)}
        </span>
        <div className="flex items-center justify-center rounded bg-[#271714] px-1 py-0.5 sm:h-6 sm:px-1.5">
          <span className="font-mono text-sm leading-none text-primary">
            {formatNumber(row.original.leverage, {
              style: 'leverage',
            })}
          </span>
        </div>
        {row.original.isolated && (
          <TooltipWrapper
            content={
              <span>
                Backed by isolated liquidity pool. See{' '}
                <Link
                  className="underline"
                  target="_blank"
                  rel="noreferrer"
                  to="https://docs.vest.exchange/overview/traders/perpetual-contract-specifications"
                  onClick={(e) => e.stopPropagation()}
                >
                  Docs
                </Link>{' '}
                for details.
              </span>
            }
          >
            <div className="flex items-center justify-center rounded bg-[#271714] px-1 py-0.5 sm:h-6 sm:px-1.5">
              <span className="font-mono text-sm/none uppercase text-primary">
                ISO
              </span>
            </div>
          </TooltipWrapper>
        )}
      </div>
    ),
    meta: {
      width: 'md:w-[25%]',
    },
  },
  {
    accessorKey: 'markPrice',
    header: 'Mark Price',
    cell: ({ row }) => {
      const priceDecimals = useStore(
        useMarketStore(row.original.symbol),
        (state) => state.marketSpec.priceDecimals,
      );
      return (
        <div className={`flex items-center`}>
          <span>{formatBigInt(row.original.markPrice, priceDecimals)}</span>
        </div>
      );
    },
    sortingFn: (a, b) => {
      const {
        marketSpec: { priceDecimals: aPriceDecimals },
      } = useMarketStore(a.original.symbol).getState();
      const {
        marketSpec: { priceDecimals: bPriceDecimals },
      } = useMarketStore(b.original.symbol).getState();
      return (
        bigIntToNum(a.original.markPrice, aPriceDecimals) -
        bigIntToNum(b.original.markPrice, bPriceDecimals)
      );
    },
    meta: {
      width: 'md:w-[12.5%]',
    },
  },
  {
    accessorKey: 'priceChange',
    header: '24h Change',
    cell: ({ row }) => {
      const priceDecimals = useStore(
        useMarketStore(row.original.symbol),
        (state) => state.marketSpec.priceDecimals,
      );
      return (
        <span
          className={cn({
            'text-red': row.original.priceChange < 0,
            'text-green': row.original.priceChange >= 0,
          })}
        >
          <span className="sm:hidden">
            {Number(row.original.priceChange) >= 0n ? '+' : '-'}
          </span>
          <span>
            {formatBigInt(row.original.priceChangePct, 4n, {
              style: 'percent',
              abs: true,
            })}
          </span>
          <span className="hidden sm:inline">
            {' '}
            /{' '}
            {formatBigInt(row.original.priceChange, priceDecimals, {
              showChange: true,
            })}
          </span>
        </span>
      );
    },
    sortingFn: (a, b) =>
      Number(a.original.priceChangePct) - Number(b.original.priceChangePct),
    meta: {
      width: 'md:w-[20%] text-right sm:text-left pr-6 md:pr-0',
    },
  },
  {
    accessorKey: 'fundingRate',
    header: '1h Funding',
    cell: ({ row }) => {
      return (
        <div className={`flex items-center`}>
          <span
            className={cn({
              'text-red': row.original.fundingRate < 0,
              'text-green': row.original.fundingRate >= 0,
            })}
          >
            {formatBigInt(row.original.fundingRate, FUNDING_RATE_DECIMALS, {
              style: 'percent',
              digits: Number(FUNDING_RATE_DECIMALS) - 2,
            })}
          </span>
        </div>
      );
    },
    meta: {
      width: 'md:w-[12.5%] hidden md:table-cell',
    },
  },
  {
    accessorKey: 'openInterest',
    header: 'Open Interest',
    cell: ({ row }) => {
      const {
        marketSpec: { sizeDecimals, priceDecimals },
      } = useMarketStore(row.original.symbol).getState();
      return typeof row.original.openInterest === 'bigint'
        ? formatBigInt(
            row.original.openInterest,
            priceDecimals + sizeDecimals,
            { digits: 2 },
          )
        : '-';
    },
    enableSorting: true,
    sortingFn: (a, b) => {
      const {
        marketSpec: {
          priceDecimals: aPriceDecimals,
          sizeDecimals: aSizeDecimals,
        },
      } = useMarketStore(a.original.symbol).getState();
      const {
        marketSpec: {
          priceDecimals: bPriceDecimals,
          sizeDecimals: bSizeDecimals,
        },
      } = useMarketStore(b.original.symbol).getState();
      return (
        (a.original.openInterest
          ? bigIntToNum(a.original.openInterest, aSizeDecimals + aPriceDecimals)
          : 0) -
        (b.original.openInterest
          ? bigIntToNum(b.original.openInterest, bSizeDecimals + bPriceDecimals)
          : 0)
      );
    },
    meta: {
      width: 'md:w-[12.5%] hidden md:table-cell',
    },
  },
  {
    accessorKey: 'volume',
    header: '24h Volume',
    cell: ({ row }) => {
      const {
        marketSpec: { sizeDecimals, priceDecimals },
      } = useMarketStore(row.original.symbol).getState();
      return typeof row.original.quoteVolume === 'bigint'
        ? formatBigInt(row.original.quoteVolume, priceDecimals + sizeDecimals, {
            digits: 2,
          })
        : '-';
    },
    sortingFn: (a, b) => {
      const {
        marketSpec: {
          priceDecimals: aPriceDecimals,
          sizeDecimals: aSizeDecimals,
        },
      } = useMarketStore(a.original.symbol).getState();
      const {
        marketSpec: {
          priceDecimals: bPriceDecimals,
          sizeDecimals: bSizeDecimals,
        },
      } = useMarketStore(b.original.symbol).getState();
      return (
        (a.original.quoteVolume
          ? bigIntToNum(a.original.quoteVolume, aSizeDecimals + aPriceDecimals)
          : 0) -
        (b.original.quoteVolume
          ? bigIntToNum(b.original.quoteVolume, bSizeDecimals + bPriceDecimals)
          : 0)
      );
    },
    meta: {
      width: 'md:w-[12.5%] hidden md:table-cell',
    },
  },
];

// Columns handle formatting b/c raw values are needed for sorting
export const MarketsTable: React.FC<MarketsTableProps> = memo(
  ({ onRowClick }) => {
    const [sorting, setSorting] = useState<SortingState>([]);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useTicker24hr({});
    useOpenInterest({});

    const markets = useMarketStores((state) => ({
      marketData: state.marketData,
      marketSpec: state.marketSpec,
    }));

    const { isFavorite } = useFavorites();
    const [category, setCategory] = useState<
      'all' | 'favorite' | SymbolCategory
    >('all');

    // Data is mapped to bigint format for accurate sorting
    const data = useMemo<MarketItem[]>(() => {
      return Object.entries(markets)
        .filter(([symbol]) => {
          if (
            !abbrFromSym(symbol)
              .toLowerCase()
              .includes(debouncedSearch.toLowerCase())
          )
            return false;
          if (category === 'all') return true;
          if (category === 'favorite') return isFavorite(symbol);
          return SYMBOL_CATEGORIES[category].includes(abbrFromSym(symbol));
        })
        .map(([symbol, market]) => ({
          symbol,
          leverage: getMaxLeverage(market.marketSpec)?.decimal,
          markPrice: market.marketData.markPrice,
          priceChange: market.marketData.priceChange,
          priceChangePct: market.marketData.priceChangePct,
          fundingRate: market.marketData.oneHrFundingRate * 100n,
          isFavorite: isFavorite(symbol),
          quoteVolume: market.marketData.quoteVolume,
          openInterest: market.marketData.openInterest,
          isolated: market.marketSpec.isolated,
          status: market.marketSpec.status,
        }))
        .sort((a, b) => {
          if (a.isFavorite && !b.isFavorite) return -1;
          if (!a.isFavorite && b.isFavorite) return 1;
          return a.symbol.localeCompare(b.symbol);
        });
    }, [debouncedSearch, category, markets]);

    const table = useReactTable({
      data,
      columns,
      state: {
        sorting,
      },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
    });

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedSearch(search);
      }, 100);

      return () => {
        clearTimeout(handler);
      };
    }, [search]);

    const categories = useMemo(() => {
      const symbols = Object.keys(markets);
      return Object.keys(SYMBOL_CATEGORIES).filter((category) =>
        symbols.some((symbol) =>
          SYMBOL_CATEGORIES[category as SymbolCategory].includes(
            abbrFromSym(symbol),
          ),
        ),
      );
    }, [markets]);

    return (
      <div className="w-full rounded-br-md border border-l-0 border-border bg-vestgrey-900">
        <div className="flex h-16 items-center justify-between gap-3 border-b border-border px-6 py-3 sm:h-auto">
          <div className="relative flex items-center gap-1.5 text-foreground">
            <Search size={18} />
            <input
              type="text"
              placeholder="SEARCH"
              autoFocus
              // ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="text-font w-full bg-vestgrey-900 p-2 font-mono placeholder:text-vestgrey-100 focus:outline-none"
            />
          </div>
        </div>
        <Tabs
          value={category}
          onValueChange={(value) => setCategory(value as SymbolCategory)}
          className="h-12"
        >
          <TabsList className="no-scrollbar h-full w-full justify-start overflow-x-auto overflow-y-hidden">
            <TabsTrigger
              className="h-full min-w-max border-t-0 px-6 text-sm"
              value="all"
            >
              All
            </TabsTrigger>
            <TabsTrigger
              className="h-full min-w-max border-t-0 px-6 text-sm"
              value="favorite"
            >
              Favorites
            </TabsTrigger>
            {categories.map((category) => (
              <TabsTrigger
                className="h-full min-w-max border-t-0 px-6 text-sm"
                value={category}
                key={category}
              >
                {category}
              </TabsTrigger>
            ))}
            <div className="h-full flex-1 border-b border-border" />
          </TabsList>
        </Tabs>
        <div className="no-scrollbar h-[328px] overflow-auto">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="text-left">
                  {headerGroup.headers.map((header, i) => (
                    <th
                      className={cn(
                        'cursor-pointer whitespace-nowrap border-b-2 border-border bg-vestgrey-900 px-2 py-3 font-mono text-sm font-normal uppercase text-foreground first:pl-6 last:pr-6 sm:px-6',
                        // @ts-ignore
                        header.column.columnDef.meta?.width,
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                      key={header.id}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {{
                        asc: <span className="pl-2">↑</span>,
                        desc: <span className="pl-2">↓</span>,
                      }[header.column.getIsSorted() as string] ?? null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              <tr className="h-3"></tr>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td
                    className="py-6 text-center text-vestgrey-100"
                    colSpan={columns.length}
                  >
                    No results.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <MarketRow
                    key={row.id}
                    row={row}
                    onRowClick={(symbol) => {
                      onRowClick(symbol);
                      setSearch('');
                    }}
                  />
                ))
              )}
              <tr className="h-3"></tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  },
  () => true,
);
